import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ScaleIcon,
  WrenchScrewdriverIcon,
  CubeIcon,
  ClockIcon,
  HashtagIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { cleanDataForAPI } from '../../utils/data-cleanup';

interface UoM {
  id: string;
  code: string;
  name: string;
  symbol?: string;
  type: string;
  description?: string;
  isBaseUnit: boolean;
  conversionFactor: number;
  decimalPlaces: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const UoMType = {
  QUANTITY: 'quantity',
  WEIGHT: 'weight',
  LENGTH: 'length',
  AREA: 'area',
  VOLUME: 'volume',
  TIME: 'time',
};

const UoMManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConverterOpen, setIsConverterOpen] = useState(false);
  const [selectedUoM, setSelectedUoM] = useState<UoM | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'grouped'>('grid');
  
  const queryClient = useQueryClient();

  // Fetch UoMs
  const { data: uomsData, isLoading } = useQuery({
    queryKey: ['uoms', searchTerm, filterType, filterActive],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterType) params.append('type', filterType);
      if (filterActive) params.append('isActive', filterActive);
      
      const response = await api.get(`/master-data/uoms?${params}`);
      return response.data;
    },
  });

  // Fetch grouped UoMs
  const { data: groupedData } = useQuery({
    queryKey: ['uomsGrouped'],
    queryFn: async () => {
      const response = await api.get('/master-data/uoms/grouped');
      return response.data;
    },
    enabled: viewMode === 'grouped',
  });

  // Create/Update UoM
  const createUpdateMutation = useMutation({
    mutationFn: async (data: any) => {
      const cleanData = cleanDataForAPI(data);
      
      if (selectedUoM) {
        return api.patch(`/master-data/uoms/${selectedUoM.id}`, cleanData);
      }
      return api.post('/master-data/uoms', cleanData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uoms'] });
      queryClient.invalidateQueries({ queryKey: ['uomsGrouped'] });
      toast.success(selectedUoM ? 'UoM updated successfully' : 'UoM created successfully');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Operation failed');
    },
  });

  // Delete UoM
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/master-data/uoms/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uoms'] });
      queryClient.invalidateQueries({ queryKey: ['uomsGrouped'] });
      toast.success('UoM deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Delete failed');
    },
  });

  // Toggle status
  const toggleStatusMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.patch(`/master-data/uoms/${id}/toggle-status`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uoms'] });
      queryClient.invalidateQueries({ queryKey: ['uomsGrouped'] });
      toast.success('Status updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Status update failed');
    },
  });

  const openModal = (uom?: UoM) => {
    setSelectedUoM(uom || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUoM(null);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case UoMType.WEIGHT:
        return <ScaleIcon className="h-5 w-5" />;
      case UoMType.LENGTH:
        return <WrenchScrewdriverIcon className="h-5 w-5" />;
      case UoMType.VOLUME:
        return <CubeIcon className="h-5 w-5" />;
      case UoMType.TIME:
        return <ClockIcon className="h-5 w-5" />;
      case UoMType.QUANTITY:
        return <HashtagIcon className="h-5 w-5" />;
      default:
        return <CubeIcon className="h-5 w-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    const typeColors = {
      [UoMType.QUANTITY]: 'bg-blue-100 text-blue-800',
      [UoMType.WEIGHT]: 'bg-purple-100 text-purple-800',
      [UoMType.LENGTH]: 'bg-green-100 text-green-800',
      [UoMType.AREA]: 'bg-yellow-100 text-yellow-800',
      [UoMType.VOLUME]: 'bg-red-100 text-red-800',
      [UoMType.TIME]: 'bg-gray-100 text-gray-800',
    };

    return typeColors[type] || 'bg-gray-100 text-gray-800';
  };

  const renderUoMCard = (uom: UoM) => (
    <Card key={uom.id} className="hover:shadow-lg transition-shadow">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {getTypeIcon(uom.type)}
            <div>
              <h3 className="font-semibold text-gray-900">{uom.name}</h3>
              <p className="text-sm text-gray-500">{uom.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(uom.type)}`}>
              {uom.type}
            </span>
            {uom.isBaseUnit && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                Base
              </span>
            )}
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {uom.symbol && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Symbol:</span>
              <span className="font-medium">{uom.symbol}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Conversion Factor:</span>
            <span className="font-medium">{uom.conversionFactor}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Decimal Places:</span>
            <span className="font-medium">{uom.decimalPlaces}</span>
          </div>
          {uom.description && (
            <div className="text-sm">
              <span className="text-gray-600">Description:</span>
              <p className="text-gray-800 mt-1">{uom.description}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <button
            onClick={() => toggleStatusMutation.mutate(uom.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
              uom.isActive
                ? 'text-green-600 hover:bg-green-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {uom.isActive ? (
              <CheckCircleIcon className="h-4 w-4" />
            ) : (
              <XCircleIcon className="h-4 w-4" />
            )}
            {uom.isActive ? 'Active' : 'Inactive'}
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => openModal(uom)}
              title="Edit"
              aria-label="Edit"
              className="p-1 hover:bg-gray-100 rounded"
            >
              <PencilIcon className="h-4 w-4 text-gray-600" />
            </button>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this UoM?')) {
                  deleteMutation.mutate(uom.id);
                }
              }}
              title="Delete"
              aria-label="Delete"
              className="p-1 hover:bg-red-50 rounded"
            >
              <TrashIcon className="h-4 w-4 text-red-600" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Unit of Measure Management</h1>
        <p className="text-gray-600">Manage units of measure and conversion factors</p>
      </div>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search UoMs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              {Object.entries(UoMType).map(([key, value]) => (
                <option key={value} value={value}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                </option>
              ))}
            </select>

            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded-lg ${
                  viewMode === 'grid'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('grouped')}
                className={`px-4 py-2 rounded-lg ${
                  viewMode === 'grouped'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Grouped
              </button>
            </div>

            <Button onClick={() => setIsConverterOpen(true)} className="flex items-center gap-2">
              <ArrowsRightLeftIcon className="h-5 w-5" />
              Converter
            </Button>

            <Button onClick={() => openModal()} className="flex items-center gap-2">
              <PlusIcon className="h-5 w-5" />
              Add UoM
            </Button>
          </div>
        </div>
      </Card>

      {/* Content based on view mode */}
      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {uomsData?.data?.map((uom: UoM) => renderUoMCard(uom))}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(UoMType).map(([typeKey, typeValue]) => (
            <Card key={typeValue}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  {getTypeIcon(typeValue)}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {typeKey.charAt(0) + typeKey.slice(1).toLowerCase()}
                  </h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(typeValue)}`}>
                    {groupedData?.[typeValue]?.length || 0} units
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedData?.[typeValue]?.map((uom: UoM) => renderUoMCard(uom))}
                </div>
                {(!groupedData?.[typeValue] || groupedData[typeValue].length === 0) && (
                  <p className="text-gray-500 text-center py-4">No units found for this type</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <UoMModal
          uom={selectedUoM}
          onClose={closeModal}
          onSubmit={(data) => createUpdateMutation.mutate(data)}
        />
      )}

      {/* Conversion Calculator */}
      {isConverterOpen && (
        <ConversionCalculator
          onClose={() => setIsConverterOpen(false)}
        />
      )}
    </div>
  );
};

// UoM Modal Component
const UoMModal: React.FC<{
  uom: UoM | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ uom, onClose, onSubmit }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: uom || {
      conversionFactor: 1,
      decimalPlaces: 2,
      isBaseUnit: false,
      isActive: true,
      type: UoMType.QUANTITY,
    },
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {uom ? 'Edit Unit of Measure' : 'Add New Unit of Measure'}
        </h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code *
              </label>
              <input
                {...register('code', { required: 'Code is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.code && (
                <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                {...register('name', { required: 'Name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Symbol
              </label>
              <input
                {...register('symbol')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type *
              </label>
              <select
                {...register('type', { required: 'Type is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(UoMType).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conversion Factor
              </label>
              <input
                type="number"
                step="0.000001"
                {...register('conversionFactor', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Decimal Places
              </label>
              <input
                type="number"
                min="0"
                max="6"
                {...register('decimalPlaces', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('isBaseUnit')}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Base Unit</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('isActive')}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {uom ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Conversion Calculator Component
const ConversionCalculator: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const [value, setValue] = useState<number>(1);
  const [fromUoMId, setFromUoMId] = useState('');
  const [toUoMId, setToUoMId] = useState('');
  const [result, setResult] = useState<any>(null);

  const { data: uomsData } = useQuery({
    queryKey: ['uoms-for-conversion'],
    queryFn: async () => {
      const response = await api.get('/master-data/uoms?limit=-1');
      return response.data;
    },
  });

  const convertMutation = useMutation({
    mutationFn: async (data: { value: number; fromUoMId: string; toUoMId: string }) => {
      const response = await api.post('/master-data/uoms/convert', data);
      return response.data;
    },
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Conversion failed');
    },
  });

  const handleConvert = () => {
    if (!fromUoMId || !toUoMId || !value) {
      toast.error('Please fill all fields');
      return;
    }
    convertMutation.mutate({ value, fromUoMId, toUoMId });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Unit Conversion Calculator</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Value to Convert
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Unit
            </label>
            <select
              value={fromUoMId}
              onChange={(e) => setFromUoMId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select Unit</option>
              {uomsData?.data?.map((uom: UoM) => (
                <option key={uom.id} value={uom.id}>
                  {uom.name} ({uom.symbol || uom.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Unit
            </label>
            <select
              value={toUoMId}
              onChange={(e) => setToUoMId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select Unit</option>
              {uomsData?.data?.map((uom: UoM) => (
                <option key={uom.id} value={uom.id}>
                  {uom.name} ({uom.symbol || uom.code})
                </option>
              ))}
            </select>
          </div>

          <Button
            onClick={handleConvert}
            className="w-full"
            disabled={convertMutation.isPending}
          >
            {convertMutation.isPending ? 'Converting...' : 'Convert'}
          </Button>

          {result && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <p className="text-sm font-medium text-green-800">
                Conversion Result:
              </p>
              <p className="text-lg font-bold text-green-900">
                {result.formula}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UoMManagement;